import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "../../lib/api";
import StatCard from "../../components/common/StatCard";
import { AlertTriangle } from "lucide-react";

export default function SellerAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    api.get(`/seller/analytics?days=${days}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load analytics"); });
    return () => { alive = false; };
  }, [days]);

  return (
    <div data-testid="seller-analytics-page">
      <div className="overline mb-2">Insights</div>
      <h1 className="font-display text-4xl mb-6">Analytics</h1>

      <div className="flex gap-2 mb-6">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`chip ${days === d ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`}
            data-testid={`days-${d}`}
          >
            Last {d} days
          </button>
        ))}
      </div>

      {error && <div className="border border-red-300 bg-red-50 text-red-800 p-3 mb-6 text-sm" data-testid="analytics-error">{error}</div>}

      {!data && !error && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 bg-white border border-[hsl(var(--kb-border))] animate-pulse" />
          ))}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="GMV" value={money(data.gmv)} testid="stat-gmv" />
            <StatCard label="Units Sold" value={data.units} testid="stat-units" />
            <StatCard label="Orders" value={data.orders} testid="stat-orders" />
            <StatCard label="Your Payout" value={money(data.payout)} testid="stat-payout" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 bg-white border border-[hsl(var(--kb-border))] p-6">
              <div className="overline mb-4">Sales · last {data.days} days</div>
              {data.series.length === 0 ? (
                <div className="py-12 text-center text-[hsl(var(--kb-muted))] text-sm">No paid orders in this window.</div>
              ) : (
                <div className="flex items-end gap-1 h-44 overflow-x-auto" data-testid="seller-sales-chart">
                  {data.series.map((d) => {
                    const max = Math.max(...data.series.map((x) => x.total), 1);
                    return (
                      <div key={d.date} className="flex-1 min-w-[20px] flex flex-col items-center gap-1">
                        <div
                          className="w-full bg-[hsl(var(--kb-primary))]/80"
                          style={{ height: `${(d.total / max) * 100}%` }}
                          title={`${d.date}: ${money(d.total)}`}
                        />
                        <div className="text-[9px] text-[hsl(var(--kb-muted))]">{d.date.slice(5)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="seller-low-stock">
              <div className="flex items-center gap-2 overline mb-4">
                <AlertTriangle size={14} /> Low stock
              </div>
              {data.low_stock.length === 0 ? (
                <div className="text-sm text-[hsl(var(--kb-muted))]">All healthy.</div>
              ) : (
                <div className="space-y-3 text-sm">
                  {data.low_stock.map((p) => (
                    <Link key={p.product_id} to={`/seller/products/${p.product_id}/edit`} className="flex justify-between hover:underline">
                      <div className="truncate">{p.title}</div>
                      <div className="text-[hsl(var(--kb-primary))] font-medium shrink-0">{p.total_stock} left</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[hsl(var(--kb-border))]">
              <div className="p-4 border-b border-[hsl(var(--kb-border))] overline">Best Sellers</div>
              {data.best_sellers.length === 0 ? (
                <div className="p-6 text-center text-[hsl(var(--kb-muted))] text-sm">No sales yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="best-sellers-table">
                  <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
                    <tr><th className="p-3">Product</th><th>Qty</th><th>Gross</th></tr>
                  </thead>
                  <tbody>
                    {data.best_sellers.map((b) => (
                      <tr key={b.product_id} className="border-t border-[hsl(var(--kb-border))]">
                        <td className="p-3">{b.title}</td>
                        <td className="p-3">{b.qty}</td>
                        <td className="p-3">{money(b.gross)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white border border-[hsl(var(--kb-border))]">
              <div className="p-4 border-b border-[hsl(var(--kb-border))] overline">Coupon Performance</div>
              {data.coupon_performance.length === 0 ? (
                <div className="p-6 text-center text-[hsl(var(--kb-muted))] text-sm">No coupons configured.</div>
              ) : (
                <table className="w-full text-sm" data-testid="coupon-perf-table">
                  <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
                    <tr><th className="p-3">Code</th><th>Used</th><th>Active</th></tr>
                  </thead>
                  <tbody>
                    {data.coupon_performance.map((c) => (
                      <tr key={c.code} className="border-t border-[hsl(var(--kb-border))]">
                        <td className="p-3 font-mono">{c.code}</td>
                        <td className="p-3">{c.used_count}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                        <td className="p-3 text-xs uppercase">{c.is_active ? "yes" : "no"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
